import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    IconButton,
    Input,
    InputAdornment,
    Dialog,
    AppBar,
    Toolbar,
    Slide,
    Card,
    CardContent,
    useTheme,
} from "@mui/material";
import { CloseIcon, SearchIcon, AccountBalanceWalletIcon } from "../../../Resources/Icons/icons";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import { getBriskWalletReportByUserId } from "../../../Service/_index";
import Loader from "../../../UiComponents/Loader/Loader";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import moment from "moment";
import wallett from "../../../Resources/Images/wallet.png";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Transaction History Table Headers
const transactionHeaders = [
    { id: "policyDate", placeMent: true, disablePadding: false, label: "Date" },
    { id: "policyNumber", placeMent: true, disablePadding: false, label: "Policy Number" },
    // { id: "customerName", placeMent: true, disablePadding: false, label: "Customer Name" },
    // { id: "totalPremium", placeMent: true, disablePadding: false, label: "Premium" },
    { id: "amount", placeMent: true, disablePadding: false, label: "Amount" },
    { id: "walletBalance", placeMent: true, disablePadding: false, label: "Balance" },
];

const BriskManageWallet = ({ open, onClose, selectedWallet }) => {
    const theme = useTheme();
    const [transactionData, setTransactionData] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openLoader, setOpenLoader] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (open && selectedWallet?.userId) {
            GetUserWalletDetails(selectedWallet.userId);
        }
    }, [open, selectedWallet]);

    const GetUserWalletDetails = async (userId) => {
        setOpenLoader(true);
        getBriskWalletReportByUserId(userId)
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error) {
                    const formattedData = res.map((row) => ({
                        ...row,
                        policyDate: row.policyDate ? moment(row.policyDate).format("DD/MM/YYYY") : "-",
                        policyNumber: (
                            <Typography
                                sx={{
                                    color:
                                        row.policyNumber === "ONLINE"
                                            ? "blue"
                                            : row.policyNumber?.includes("CANCELED")
                                                ? "orange"
                                                : "inherit",
                                    fontWeight: row.policyNumber === "ONLINE" ? "bold" : "normal",
                                    fontSize: "14px",
                                }}
                            >
                                {row.policyNumber || "-"}
                            </Typography>
                        ),
                        customerName: row.customerName || "-",
                        totalPremium: row.totalPremium
                            ? `₹ ${Number(row.totalPremium).toLocaleString("en-IN")}`
                            : "-",
                        amount: (
                            <Typography
                                sx={{
                                    color: row.amount >= 0 ? "green" : "red",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            >
                                {row.amount >= 0
                                    ? `+ ₹ ${Math.abs(row.amount)?.toLocaleString("en-IN") || 0}`
                                    : `- ₹ ${Math.abs(row.amount)?.toLocaleString("en-IN") || 0}`}
                            </Typography>
                        ),
                        walletBalance: (
                            <Typography
                                sx={{
                                    color: row.walletBalance >= 0 ? "green" : "red",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                }}
                            >
                                ₹ {row.walletBalance?.toLocaleString("en-IN") || 0}
                            </Typography>
                        ),
                    }));
                    setTransactionData(formattedData);
                    setDisplayData(formattedData);
                } else {
                    setTransactionData([]);
                    setDisplayData([]);
                }
            })
            .catch((err) => {
                setOpenLoader(false);
                ToastError("Failed to load transaction history");
            });
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        if (term === "") {
            setDisplayData(transactionData);
        } else {
            const filtered = transactionData.filter((item) =>
                Object.values(item).some((val) =>
                    // Since elements are likely React nodes now, simple search might fail on formatted columns
                    // We should search on raw data, but we formatted it.
                    // Ideally we format in MasterCommonTable? OR search raw data.
                    // For simplicity, we search what strings we have or re-fetch raw?
                    // Let's assume MasterCommonTable expects formatted nodes? Yes.
                    // So filtering is tricky.
                    // We can check item.customerName (string) and maybe others?
                    // Or keep rawData state.
                    // For now, simple string check on likely string fields:
                    String(item.customerName).toLowerCase().includes(term) ||
                    String(item.totalPremium).toLowerCase().includes(term)
                )
            );
            setDisplayData(filtered);
        }
        setPage(0);
    };

    // Improved search logic: keep raw data separately if needed, but for now simple filter on specific fields
    useEffect(() => {
        if (!searchTerm) {
            setDisplayData(transactionData);
            return;
        }
        const lowerTerm = searchTerm.toLowerCase();
        const filtered = transactionData.filter(item => {
            // Accessing the string content from JSX elements is hard. 
            // We should have processed raw data into specific search keys if possible.
            // But existing code just checks basic fields. 
            // Let's rely on fields that are strings.
            return (
                String(item.customerName).toLowerCase().includes(lowerTerm) // Customer Name is string
            );
        });
        setDisplayData(filtered);
    }, [searchTerm, transactionData]);


    const tableConfig = {
        isAction: false,
        isView: false,
        isEdit: false,
        isDelete: false,
        isSearch: false,
        isUpload: false,
        isFileDownload: false,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            TransitionComponent={Transition}
            PaperProps={{
                sx: {
                    minHeight: "80vh",
                    maxHeight: "90vh",
                    borderRadius: "12px",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                }
            }}
        >
            <AppBar sx={{ position: "relative", bgcolor: "#1E3A5F", borderRadius: "12px 12px 0 0" }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Wallet Statement - {selectedWallet?.userName || "User"}
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ padding: "20px", display: "flex", flexDirection: "column", gap: 3, bgcolor: "#f5f5f5", height: "100%", overflowY: "auto" }}>
                {/* Balance Card and Search */}
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: "12px", boxShadow: "0px 4px 20px rgba(0,0,0,0.05)" }}>
                            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, padding: "16px !important" }}>
                                <Box sx={{
                                    bgcolor: "#E3F2FD",
                                    p: 1.5,
                                    borderRadius: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <img src={wallett} width="24px" height="24px" alt="wallet" />
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        Current Balance
                                    </Typography>
                                    <Typography variant="h5" color={selectedWallet?.balance >= 0 ? "black" : "red"} fontWeight={700}>
                                        ₹ {selectedWallet?.balance?.toLocaleString("en-IN") || 0}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        {/* <Input
                            fullWidth
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disableUnderline
                            placeholder="Search by Customer Name..."
                            sx={{
                                bgcolor: "white",
                                borderRadius: "8px",
                                padding: "8px 16px",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                                border: "1px solid #e0e0e0",
                                "&:hover": { border: "1px solid #1E3A5F" },
                            }}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#9e9e9e" }} />
                                </InputAdornment>
                            }
                        /> */}
                    </Grid>
                </Grid>

                {/* Table */}
                <Card sx={{ borderRadius: "12px", boxShadow: "0px 4px 20px rgba(0,0,0,0.05)", overflow: "hidden" }}>
                    <MasterCommonTable
                        data={displayData}
                        columns={transactionHeaders}
                        tableConfig={tableConfig}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </Card>
            </Box>

            <Loader open={openLoader} />
        </Dialog>
    );
};

export default BriskManageWallet;
